import axios from 'axios';

/**
 * 개발 서버에서는 상대 경로로 보내 CRA 프록시(setupProxy.js)를 태운다.
 *
 * 절대 주소로 보내면 localhost:3000 → api.castar.co.kr 가 cross-site라,
 * SameSite=Strict로 내려오는 로그인 쿠키가 다음 요청부터 실리지 않는다.
 * 로그인은 200이 떨어지는데 그 뒤로 세션이 없는 것처럼 구는 증상이 이것이다.
 * 프록시를 타면 브라우저가 보기에 localhost → localhost라 Strict로도 쿠키가 간다.
 */
const BASE_URL = process.env.NODE_ENV === 'development'
  ? ''
  : (process.env.REACT_APP_API_BASE_URL || 'https://api.castar.co.kr');

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

/** 세션이 끝났음을 앱에 알리는 이벤트 — AuthContext가 받아 로그인 상태를 비운다 */
export const AUTH_EXPIRED_EVENT = 'castar:auth-expired';

/**
 * 401이 나도 갱신을 시도하지 않는 요청.
 * 갱신·로그아웃 자신이 401이면 갱신하러 가는 순간 무한히 돈다.
 * 로그인·가입의 401은 세션 만료가 아니라 자격증명 오류다.
 */
const NO_REFRESH_PATHS = [
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/login',
  '/api/auth/login',
  '/api/auth/nlogin',
  '/api/auth/register',
];

/** 요청 주소에서 경로만 뽑는다. 포함 여부로 비교하면 /api/auth/email-check도 걸린다. */
export function pathOf(config) {
  try {
    return new URL(config?.url || '', config?.baseURL || BASE_URL).pathname;
  } catch {
    return config?.url || '';
  }
}

let refreshing = null;

/**
 * 토큰 갱신 — 동시에 한 번만.
 *
 * 화면을 열면 API 여러 개가 함께 나가고, 토큰이 만료된 순간 그 요청들이 동시에 401을 받는다.
 * 각자 갱신하면 첫 갱신이 리프레시 토큰을 폐기해 두 번째 갱신이 반드시 실패하고,
 * 멀쩡한 사용자가 로그아웃된다. 진행 중인 갱신이 있으면 새로 부르지 않고 그 결과를 함께 기다린다.
 *
 * 관리자 API(adminHttp)도 같은 쿠키를 쓰므로 이 함수를 공유해야 한다.
 */
export function refreshOnce() {
  if (!refreshing) {
    refreshing = axios
      .post(`${BASE_URL}/api/auth/refresh`, null, { withCredentials: true })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

let expiredNotified = false;

/**
 * 세션 종료 처리. 여러 요청이 동시에 실패해도 한 번만 한다.
 *
 * 화면 이동도, 저장소 정리도 여기서 하지 않는다. AuthContext가 '로그인해 있던 경우에만' 한다.
 * 비로그인 방문자도 앱을 켤 때 /api/users/me → 401 → 갱신 실패를 거치므로,
 * 여기서 지우면 방문할 때마다 저장소가 비고, 이동시키면 첫 화면에서 로그인으로 끌려간다.
 */
export function notifyAuthExpired(refreshError) {
  if (expiredNotified) return;
  expiredNotified = true;
  setTimeout(() => { expiredNotified = false; }, 0);

  // 쿠키가 남아 있는데 무효인 경우만 서버에 지워달라고 한다.
  // 쿠키 자체가 없으면(비로그인 방문자) 부를 이유가 없다.
  if (refreshError?.response?.data?.code === 'REFRESH_TOKEN_INVALID') {
    axios.post(`${BASE_URL}/api/auth/logout`, null, { withCredentials: true }).catch(() => {});
  }

  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/**
 * 401 → (갱신 제외 경로가 아니면) 갱신 → 성공하면 원래 요청을 다시 보낸다.
 * 403은 갱신하지 않는다 — 권한 문제라 갱신해도 풀리지 않는다.
 */
export async function handleUnauthorized(error, retry) {
  const config = error?.config;
  if (error?.response?.status !== 401 || !config) return Promise.reject(error);

  const path = pathOf(config);
  if (NO_REFRESH_PATHS.includes(path)) {
    // 갱신 자체가 401이면 세션이 끝난 것. 로그인·가입 401은 입력 오류라 그대로 돌려준다.
    if (path === '/api/auth/refresh') notifyAuthExpired(error);
    return Promise.reject(error);
  }

  // 갱신 후 다시 보냈는데도 401이면 더 시도하지 않는다
  if (config._retried) {
    notifyAuthExpired();
    return Promise.reject(error);
  }

  try {
    await refreshOnce();
  } catch (refreshError) {
    notifyAuthExpired(refreshError);
    return Promise.reject(error);
  }

  config._retried = true;
  return retry(config);
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => handleUnauthorized(error, (config) => axiosInstance(config)),
);

/** 브라우저에 남은 로그인 흔적을 지운다. 사용자 선호(쿠키 동의·저장한 이메일 등)는 남긴다. */
export function clearAuthStorage() {
  const keep = ['cookie_consent', 'saved_email', 'auto_login', 'castar_post_login_notice_hide_date', 'community_read'];
  const saved = keep.map(key => [key, localStorage.getItem(key)]);
  localStorage.clear();
  saved.forEach(([key, value]) => { if (value != null) localStorage.setItem(key, value); });
}

export default axiosInstance;
